import React from 'react';
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { JourneyMapDraftLink } from '../../../types/journeyMap';

type LinkType = JourneyMapDraftLink['link_type'];
type ConditionType = NonNullable<JourneyMapDraftLink['condition_type']>;

type JourneyMapRuleProps = {
  link: JourneyMapDraftLink;
  onChange: (changes: Partial<JourneyMapDraftLink>) => void;
};

const FALLBACK_BEHAVIOR_OPTIONS = [
  { value: 'default_path_only', label: 'Default Path Only' },
  { value: 'allow_other_alternates', label: 'Allow Other Alternate Paths' },
  { value: 'lock_node', label: 'Lock This Node' },
  { value: 'route_to_fallback_link', label: 'Route to Fallback Link' },
];

const TEST_OUT_RULE_BASIS_OPTIONS = [
  { value: 'score', label: 'Score' },
  { value: 'attempts', label: 'Attempt Count' },
  { value: 'completion_status', label: 'Completion Status' },
  { value: 'provider_result', label: 'Provider Result' },
];

const SCORE_OPERATOR_OPTIONS = [
  { value: 'gte', label: '>=' },
  { value: 'gt', label: '>' },
  { value: 'lte', label: '<=' },
  { value: 'lt', label: '<' },
  { value: 'eq', label: '=' },
];

const ATTEMPT_OPERATOR_OPTIONS = [
  { value: 'eq', label: '=' },
  { value: 'gte', label: '>=' },
  { value: 'lte', label: '<=' },
];

const STATUS_OPTIONS = [
  { value: 'passed', label: 'Passed' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'incomplete', label: 'Incomplete' },
];

function JourneyMapRule({ link, onChange }: JourneyMapRuleProps) {
  const conditionConfig: Record<string, any> = link.condition_config || {};

  const branchGroup = String(conditionConfig.branch_group || '');
  const fallbackBehavior = String(conditionConfig.fallback_behavior || '');
  const completionEquivalent = !!conditionConfig.completion_equivalent;
  const lockAfterExhausted = !!conditionConfig.lock_after_exhausted;
  const isActive = link.is_active ?? true;
  const priority =
    link.priority === undefined || link.priority === null ? '' : String(link.priority);

  const ruleExpression = String(conditionConfig.rule_expression || '');
  const scoreValue =
    conditionConfig.score === undefined || conditionConfig.score === null
      ? ''
      : String(conditionConfig.score);
  const statusValue = String(conditionConfig.status || '');

  const testOutRuleBasis = String(conditionConfig.rule_basis || 'score');
  const testOutOperator = String(
    conditionConfig.operator ||
    (testOutRuleBasis === 'score'
      ? 'gte'
      : testOutRuleBasis === 'attempts'
        ? 'lte'
        : 'equals'),
  );
  const testOutValue =
    conditionConfig.value === undefined || conditionConfig.value === null
      ? ''
      : String(conditionConfig.value);
  const maxAttempts =
    conditionConfig.max_attempts === undefined || conditionConfig.max_attempts === null
      ? ''
      : String(conditionConfig.max_attempts);
  const providerKey = String(conditionConfig.provider_key || '');

  const showBranchGroup =
    link.link_type === 'alternate' ||
    link.link_type === 'conditional' ||
    link.link_type === 'test_out' ||
    link.link_type === 'optional';

  const showCompletionEquivalent = link.link_type === 'alternate';

  const showFallbackBehavior =
    link.link_type === 'conditional' ||
    link.link_type === 'test_out' ||
    link.link_type === 'optional' ||
    link.link_type === 'fallback';

  const showConditionType = !['default', 'pass', 'fail', 'skip'].includes(link.link_type);

  const showScoreField =
    link.condition_type === 'score_gte' ||
    link.condition_type === 'score_gt' ||
    link.condition_type === 'score_lte' ||
    link.condition_type === 'score_lt';

  const showStatusField = link.condition_type === 'status_equals';
  const showGenericRuleExpression =
    link.link_type !== 'test_out' && link.condition_type === 'rule';

  const updateConditionConfig = (changes: Record<string, any>) => {
    onChange({
      condition_config: {
        ...(link.condition_config || {}),
        ...changes,
      },
    });
  };

  const replaceConditionConfig = (nextConfig: Record<string, any>) => {
    onChange({
      condition_config: nextConfig,
    });
  };

  const handleLinkTypeChange = (value: LinkType) => {
    const nextConditionConfig: Record<string, any> = { ...(link.condition_config || {}) };
    let nextConditionType: ConditionType = link.condition_type || 'always';

    if (value === 'alternate') {
      nextConditionType = 'always';
      nextConditionConfig.completion_equivalent =
        typeof nextConditionConfig.completion_equivalent === 'boolean'
          ? nextConditionConfig.completion_equivalent
          : true;
    }

    if (value === 'conditional') {
      nextConditionType = nextConditionType === 'always' ? 'rule' : nextConditionType;
    }

    if (value === 'test_out') {
      nextConditionType = 'rule';
      nextConditionConfig.rule_basis = nextConditionConfig.rule_basis || 'score';
      nextConditionConfig.operator = nextConditionConfig.operator || 'gte';
      if (nextConditionConfig.value === undefined) nextConditionConfig.value = 80;
      if (nextConditionConfig.max_attempts === undefined) nextConditionConfig.max_attempts = 2;
      if (nextConditionConfig.lock_after_exhausted === undefined) {
        nextConditionConfig.lock_after_exhausted = true;
      }
      delete nextConditionConfig.rule_expression;
      delete nextConditionConfig.score;
      delete nextConditionConfig.status;
    }

    if (value === 'optional') {
      if (!nextConditionConfig.fallback_behavior) {
        nextConditionConfig.fallback_behavior = 'allow_other_alternates';
      }
    }

    onChange({
      link_type: value,
      condition_type: nextConditionType,
      condition_config: nextConditionConfig,
    });
  };

  const handleConditionTypeChange = (value: ConditionType) => {
    const nextConfig: Record<string, any> = { ...(link.condition_config || {}) };

    if (value !== 'status_equals') {
      delete nextConfig.status;
    }

    if (
      value !== 'score_gte' &&
      value !== 'score_gt' &&
      value !== 'score_lte' &&
      value !== 'score_lt'
    ) {
      delete nextConfig.score;
    }

    if (value !== 'rule' || link.link_type === 'test_out') {
      delete nextConfig.rule_expression;
    }

    onChange({
      condition_type: value,
      condition_config: nextConfig,
    });
  };

  const handleTestOutBasisChange = (basis: string) => {
    const nextConfig: Record<string, any> = {
      ...(link.condition_config || {}),
      rule_basis: basis,
    };

    if (basis === 'score') {
      nextConfig.operator = 'gte';
      if (nextConfig.value === undefined || nextConfig.value === null || nextConfig.value === '') {
        nextConfig.value = 80;
      }
      delete nextConfig.provider_key;
    } else if (basis === 'attempts') {
      nextConfig.operator = 'lte';
      if (nextConfig.value === undefined || nextConfig.value === null || nextConfig.value === '') {
        nextConfig.value = 2;
      }
      delete nextConfig.provider_key;
    } else if (basis === 'completion_status') {
      nextConfig.operator = 'equals';
      nextConfig.value = nextConfig.value || 'passed';
      delete nextConfig.provider_key;
    } else if (basis === 'provider_result') {
      nextConfig.operator = 'equals';
      nextConfig.value = nextConfig.value || '';
    }

    replaceConditionConfig(nextConfig);
    onChange({
      condition_type: 'rule',
    });
  };

  return (
    <Stack spacing={2}>
      <FormControl size="small" fullWidth>
        <InputLabel id="side-link-type-label">Link Type</InputLabel>
        <Select
          labelId="side-link-type-label"
          label="Link Type"
          value={(link.link_type || 'default') as LinkType}
          onChange={(e) => handleLinkTypeChange(e.target.value as LinkType)}
        >
          <MenuItem value="default">Default</MenuItem>
          <MenuItem value="alternate">Alternate Successful Path</MenuItem>
          <MenuItem value="conditional">Conditional Path</MenuItem>
          <MenuItem value="test_out">Test-Out Path</MenuItem>
          <MenuItem value="optional">Optional</MenuItem>
          <MenuItem value="fallback">Fallback</MenuItem>
          <MenuItem value="pass">Pass</MenuItem>
          <MenuItem value="fail">Fail</MenuItem>
          <MenuItem value="skip">Skip</MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Label"
        size="small"
        value={link.label || ''}
        onChange={(e) => onChange({ label: e.target.value })}
        fullWidth
      />

      <TextField
        label="Priority"
        size="small"
        type="number"
        value={priority}
        onChange={(e) =>
          onChange({
            priority: e.target.value ? Number(e.target.value) : 0,
          })
        }
        fullWidth
      />

      <FormControlLabel
        control={
          <Switch
            checked={isActive}
            onChange={(e) => onChange({ is_active: e.target.checked })}
          />
        }
        label="Active"
      />

      <FormControl size="small" fullWidth>
        <InputLabel id="edge-shape-label">Edge Shape</InputLabel>
        <Select
          labelId="edge-shape-label"
          label="Edge Shape"
          value={link.edge_shape || 'smoothstep'}
          onChange={(e) =>
            onChange({
              edge_shape: e.target.value as any,
            })
          }
        >
          <MenuItem value="smoothstep">Squared</MenuItem>
          <MenuItem value="straight">Straight</MenuItem>
          <MenuItem value="bezier">Curved</MenuItem>
          <MenuItem value="step">Stepped</MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Edge Thickness"
        size="small"
        type="number"
        value={link.edge_thickness ?? ''}
        onChange={(e) =>
          onChange({
            edge_thickness: e.target.value === '' ? null : Number(e.target.value),
          })
        }
        fullWidth
      />

      <FormControl size="small" fullWidth>
        <InputLabel id="edge-dash-style-label">Dash Style</InputLabel>
        <Select
          labelId="edge-dash-style-label"
          label="Dash Style"
          value={link.edge_dash_style || 'solid'}
          onChange={(e) =>
            onChange({
              edge_dash_style: e.target.value as any,
            })
          }
        >
          <MenuItem value="solid">Solid</MenuItem>
          <MenuItem value="dashed">Dashed</MenuItem>
          <MenuItem value="dotted">Dotted</MenuItem>
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Switch
            checked={!!link.edge_animated}
            onChange={(e) =>
              onChange({
                edge_animated: e.target.checked,
              })
            }
          />
        }
        label="Animate Edge"
      />

      {showConditionType && link.link_type !== 'test_out' && (
        <FormControl size="small" fullWidth>
          <InputLabel id="condition-type-label">Condition Type</InputLabel>
          <Select
            labelId="condition-type-label"
            label="Condition Type"
            value={(link.condition_type || 'always') as ConditionType}
            onChange={(e) => handleConditionTypeChange(e.target.value as ConditionType)}
          >
            <MenuItem value="always">Always</MenuItem>
            <MenuItem value="rule">Rule</MenuItem>
            <MenuItem value="status_equals">Status Equals</MenuItem>
            <MenuItem value="score_gte">Score Greater Than or Equal</MenuItem>
            <MenuItem value="score_gt">Score Greater Than</MenuItem>
            <MenuItem value="score_lte">Score Less Than or Equal</MenuItem>
            <MenuItem value="score_lt">Score Less Than</MenuItem>
          </Select>
        </FormControl>
      )}

      {showBranchGroup && (
        <TextField
          label="Branch Group (optional)"
          size="small"
          value={branchGroup}
          onChange={(e) =>
            updateConditionConfig({
              branch_group: e.target.value,
            })
          }
          fullWidth
        />
      )}

      {showCompletionEquivalent && (
        <FormControlLabel
          control={
            <Switch
              checked={completionEquivalent}
              onChange={(e) =>
                updateConditionConfig({
                  completion_equivalent: e.target.checked,
                })
              }
            />
          }
          label="Counts as Completion-Equivalent Path"
        />
      )}

      {link.link_type === 'test_out' ? (
        <>
          <FormControl size="small" fullWidth>
            <InputLabel id="test-out-basis-label">Test-Out Basis</InputLabel>
            <Select
              labelId="test-out-basis-label"
              label="Test-Out Basis"
              value={testOutRuleBasis}
              onChange={(e) => handleTestOutBasisChange(String(e.target.value))}
            >
              {TEST_OUT_RULE_BASIS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(testOutRuleBasis === 'score' || testOutRuleBasis === 'attempts') && (
            <FormControl size="small" fullWidth>
              <InputLabel id="test-out-operator-label">Operator</InputLabel>
              <Select
                labelId="test-out-operator-label"
                label="Operator"
                value={testOutOperator}
                onChange={(e) =>
                  updateConditionConfig({
                    operator: String(e.target.value),
                  })
                }
              >
                {(testOutRuleBasis === 'score'
                    ? SCORE_OPERATOR_OPTIONS
                    : ATTEMPT_OPERATOR_OPTIONS
                ).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {testOutRuleBasis === 'score' && (
            <TextField
              label="Passing Score"
              size="small"
              type="number"
              value={testOutValue}
              onChange={(e) =>
                updateConditionConfig({
                  value: e.target.value === '' ? null : Number(e.target.value),
                })
              }
              fullWidth
            />
          )}

          {testOutRuleBasis === 'attempts' && (
            <TextField
              label="Attempt Count"
              size="small"
              type="number"
              value={testOutValue}
              onChange={(e) =>
                updateConditionConfig({
                  value: e.target.value === '' ? null : Number(e.target.value),
                })
              }
              fullWidth
            />
          )}

          {testOutRuleBasis === 'completion_status' && (
            <FormControl size="small" fullWidth>
              <InputLabel id="test-out-status-label">Required Status</InputLabel>
              <Select
                labelId="test-out-status-label"
                label="Required Status"
                value={testOutValue || 'passed'}
                onChange={(e) =>
                  updateConditionConfig({
                    operator: 'equals',
                    value: String(e.target.value),
                  })
                }
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {testOutRuleBasis === 'provider_result' && (
            <>
              <TextField
                label="Provider Result Key"
                size="small"
                value={providerKey}
                onChange={(e) =>
                  updateConditionConfig({
                    provider_key: e.target.value,
                  })
                }
                fullWidth
              />
              <TextField
                label="Expected Value"
                size="small"
                value={testOutValue}
                onChange={(e) =>
                  updateConditionConfig({
                    operator: 'equals',
                    value: e.target.value,
                  })
                }
                fullWidth
              />
            </>
          )}

          <TextField
            label="Max Attempts"
            size="small"
            type="number"
            value={maxAttempts}
            onChange={(e) =>
              updateConditionConfig({
                max_attempts: e.target.value === '' ? null : Number(e.target.value),
              })
            }
            fullWidth
          />
        </>
      ) : (
        <>
          {showStatusField && (
            <TextField
              label="Required Status"
              size="small"
              value={statusValue}
              onChange={(e) =>
                updateConditionConfig({
                  status: e.target.value,
                })
              }
              fullWidth
            />
          )}

          {showScoreField && (
            <TextField
              label="Score Threshold"
              size="small"
              type="number"
              value={scoreValue}
              onChange={(e) =>
                updateConditionConfig({
                  score: e.target.value ? Number(e.target.value) : null,
                })
              }
              fullWidth
            />
          )}

          {showGenericRuleExpression && (
            <TextField
              label="Rule Expression"
              size="small"
              value={ruleExpression}
              onChange={(e) =>
                updateConditionConfig({
                  rule_expression: e.target.value,
                })
              }
              fullWidth
              multiline
              minRows={3}
            />
          )}
        </>
      )}

      {showFallbackBehavior && (
        <FormControl size="small" fullWidth>
          <InputLabel id="fallback-behavior-label">Fallback Behavior</InputLabel>
          <Select
            labelId="fallback-behavior-label"
            label="Fallback Behavior"
            value={fallbackBehavior}
            onChange={(e) =>
              updateConditionConfig({
                fallback_behavior: String(e.target.value),
              })
            }
          >
            {FALLBACK_BEHAVIOR_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {(link.link_type === 'conditional' || link.link_type === 'test_out') && (
        <FormControlLabel
          control={
            <Switch
              checked={lockAfterExhausted}
              onChange={(e) =>
                updateConditionConfig({
                  lock_after_exhausted: e.target.checked,
                })
              }
            />
          }
          label="Lock After Exhausted"
        />
      )}
    </Stack>
  );
}

export default JourneyMapRule;